import { message } from 'antd'; // Hoặc library toast đang dùng
import {
  notificationApi,
  useGetNotificationUnreadCountQuery,
} from 'features/notification/query';
import { isFirebaseConfigured } from 'features/push-notification/constants';
import {
  isPushSupported,
  isVapidConfigured,
  subscribeToPush,
} from 'features/push-notification/helpers';
import { useSavePushSubscriptionMutation } from 'features/push-notification/query';
import { currentUserSelector } from 'features/user/selectors';
import { onMessage } from 'firebase/messaging';
import { getFirebaseMessaging } from 'lib/firebase';
import { QueryTags } from 'lib/rtkQuery/constants';
import { useAppDispatch, useAppSelector } from 'lib/stores/hook';
import { useEffect, useRef } from 'react';

const FcmTokenStorageKey = 'FCMToken';
const NotificationBroadcastChannel = 'channel4';

const stripHtml = (text: string) => text.replace(/<[^>]*>/g, '');

/**
 * Đăng ký FCM token lên BE + lắng nghe foreground / SW broadcast.
 * Mount trong MainLayout — re-bind khi đổi user (cùng browser 1 FCM token).
 */
export const useAutoPushSubscribe = (): void => {
  const [savePushSubscription] = useSavePushSubscriptionMutation();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(currentUserSelector);
  const boundUsernameRef = useRef<string | null>(null);
  const unsubscribeOnMessageRef = useRef<(() => void) | undefined>();
  const channelRef = useRef<BroadcastChannel | undefined>();

  const { data: unreadCount } = useGetNotificationUnreadCountQuery(undefined, {
    pollingInterval: 60_000,
    refetchOnFocus: true,
  });

  useEffect(() => {
    const username = currentUser?.username;
    if (!username) return;
    if (!isPushSupported() || !isVapidConfigured() || !isFirebaseConfigured())
      return;
    if (boundUsernameRef.current === username) return;

    let cancelled = false;

    const refreshUnread = () => {
      void dispatch(
        notificationApi.util.invalidateTags([
          QueryTags.Notifications,
          QueryTags.NotificationUnreadCount,
        ])
      );
    };

    const register = async () => {
      try {
        const payload = await subscribeToPush();
        if (cancelled || !payload) return;

        localStorage.setItem(FcmTokenStorageKey, payload.token);

        const result = await savePushSubscription(payload);
        if (cancelled) return;

        if ('error' in result && result.error) {
          console.error(
            '[FCM] save token failed for user=',
            username,
            result.error
          );
          return;
        }

        boundUsernameRef.current = username;

        const messaging = await getFirebaseMessaging();
        if (!messaging || cancelled) return;

        unsubscribeOnMessageRef.current?.();
        unsubscribeOnMessageRef.current = onMessage(
          messaging,
          async fgPayload => {
            const title =
              fgPayload.notification?.title ||
              fgPayload.data?.title ||
              'Thông báo mới';
            const body = stripHtml(
              fgPayload.notification?.body ||
                fgPayload.data?.body ||
                fgPayload.data?.content ||
                'Bạn có thông báo mới'
            );

            message.info(`Thông báo mới: ${body}`);

            try {
              const reg = await navigator.serviceWorker.getRegistration();
              if (reg && Notification.permission === 'granted') {
                await reg.showNotification(title, {
                  body,
                  icon: '/pwa-192x192.png',
                  badge: '/pwa-64x64.png',
                  data: fgPayload.data ?? {},
                });
              }
            } catch {
              // ignore showNotification errors in foreground
            }

            refreshUnread();
          }
        );

        if (!channelRef.current) {
          try {
            channelRef.current = new BroadcastChannel(
              NotificationBroadcastChannel
            );
            channelRef.current.onmessage = () => refreshUnread();
          } catch {
            // ignore
          }
        }
      } catch (err) {
        console.error('[FCM] register failed for user=', username, err);
      }
    };

    void register();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.username, dispatch, savePushSubscription]);

  useEffect(() => {
    return () => {
      unsubscribeOnMessageRef.current?.();
      channelRef.current?.close();
      channelRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    if (!('setAppBadge' in navigator)) return;
    const total = unreadCount?.total ?? 0;
    if (total > 0) {
      void navigator.setAppBadge?.(total)?.catch(() => {});
    } else {
      void navigator.clearAppBadge?.()?.catch(() => {});
    }
  }, [unreadCount?.total]);
};
